/**
 * Upsell dialog (docs/14): explains the feature, names the plan from the 402 payload
 * (`plan_hint`), links to billing (`upgrade_url`). Rendered by the shell from a 402
 * ENTITLEMENT_MISSING problem or from a locked nav item; strings come from the caller's t().
 */
import { Lock } from 'lucide-react';
import { Button } from './button';
import { Dialog } from './dialog';

export type UpsellPayload = {
  feature: string;
  reason?: string;
  planHint?: { code: string; name: string } | null;
  upgradeUrl?: string | null;
};

export type UpsellDialogProps = {
  open: boolean;
  onOpenChange(open: boolean): void;
  payload: UpsellPayload | null;
  labels: {
    title: string;
    /** Sentence describing the feature (already includes the feature name). */
    body: string;
    /** e.g. "Included in Pro" (null when the plan is unknown). */
    plan: string | null;
    upgrade: string;
    close: string;
  };
  onUpgrade?(url: string): void;
};

export function UpsellDialog({ open, onOpenChange, payload, labels, onUpgrade }: UpsellDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="inline-flex items-center gap-2">
          <Lock aria-hidden className="size-4 text-[var(--oex-purple)]" />
          {labels.title}
        </span>
      }
      closeLabel={labels.close}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {labels.close}
          </Button>
          {payload?.upgradeUrl ? (
            <Button
              variant="primary"
              onClick={() => {
                if (onUpgrade) onUpgrade(payload.upgradeUrl!);
                else window.location.assign(payload.upgradeUrl!);
              }}
            >
              {labels.upgrade}
            </Button>
          ) : null}
        </>
      }
    >
      <p className="text-sm">{labels.body}</p>
      {payload ? (
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <code className="font-data rounded-[2px] border border-border px-1.5 py-0.5 text-xs">
            {payload.feature}
          </code>
          {labels.plan ? <span data-testid="upsell-plan">{labels.plan}</span> : null}
        </p>
      ) : null}
    </Dialog>
  );
}
